/**
 * Client Invoices Page
 * Payment Application / AIA G702/G703 Billing System
 * BuilderBel Construction Project Management System
 */

import {
  BadgeCheck,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Download,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { ClientInvoiceDrawer } from '../components/clientInvoices';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useCertifyClientInvoiceMutation,
  useDeleteClientInvoiceMutation,
  useGetClientInvoicesQuery,
  useRecordClientInvoicePaymentMutation,
  useRejectClientInvoiceMutation,
  useSubmitClientInvoiceMutation,
} from '../services/clientInvoiceApi';
import type {
  PaymentApplication,
  PaymentApplicationStatus,
} from '../types/clientInvoice';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'certified', label: 'Certified' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'void', label: 'Void' },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusVariant = (
  status: PaymentApplicationStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'certified':
      return 'info';
    case 'submitted':
    case 'reviewed':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'void':
      return 'neutral';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: PaymentApplicationStatus): string => {
  const labels: Record<PaymentApplicationStatus, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
    certified: 'Certified',
    paid: 'Paid',
    rejected: 'Rejected',
    void: 'Void',
  };
  return labels[status] || status;
};

const getPreparedByName = (
  preparedBy: PaymentApplication['preparedBy']
): string => {
  if (!preparedBy) return '-';
  if (typeof preparedBy === 'string') return preparedBy;
  return preparedBy.name || `${preparedBy.firstName || ''} ${preparedBy.lastName || ''}`.trim() || preparedBy.email || '-';
};

// ============================================================================
// RECORD PAYMENT MODAL
// ============================================================================

interface RecordPaymentModalProps {
  application: PaymentApplication;
  onConfirm: (paidAmount: number, paidDate: string, paymentReference: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  application,
  onConfirm,
  onCancel,
  isLoading,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [paidAmount, setPaidAmount] = useState(application.currentPaymentDue);
  const [paidDate, setPaidDate] = useState(today);
  const [paymentReference, setPaymentReference] = useState('');

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" onClick={onCancel} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center space-x-3 mb-5">
            <div className="flex items-center justify-center w-10 h-10 bg-green-50 rounded-full">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Record Payment</h3>
              <p className="text-xs text-gray-500">
                Application {application.applicationNumber}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Amount Paid <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={paidAmount}
                onChange={e => setPaidAmount(Number(e.target.value))}
                className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Current payment due: {formatCurrency(application.currentPaymentDue)}
              </p>
            </div>

            <div>
              <DatePicker
                label="Payment Date"
                value={paidDate}
                onChange={date => setPaidDate(date || '')}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Payment Reference / Check #
              </label>
              <input
                type="text"
                value={paymentReference}
                onChange={e => setPaymentReference(e.target.value)}
                placeholder="Check number, wire reference..."
                className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(paidAmount, paidDate, paymentReference)}
              disabled={isLoading || !paidDate || paidAmount <= 0}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isLoading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ============================================================================
// CERTIFY MODAL
// ============================================================================

interface CertifyModalProps {
  application: PaymentApplication;
  onConfirm: (certifiedDate: string, notes: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const CertifyModal: React.FC<CertifyModalProps> = ({
  application,
  onConfirm,
  onCancel,
  isLoading,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [certifiedDate, setCertifiedDate] = useState(today);
  const [notes, setNotes] = useState('');

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" onClick={onCancel} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center space-x-3 mb-5">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-50 rounded-full">
              <BadgeCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Certify Payment Application</h3>
              <p className="text-xs text-gray-500">{application.applicationNumber}</p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg px-4 py-3 mb-4">
            <p className="text-xs text-blue-700">
              Amount to certify:{' '}
              <span className="font-bold">{formatCurrency(application.currentPaymentDue)}</span>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <DatePicker
                label="Certification Date"
                value={certifiedDate}
                onChange={date => setCertifiedDate(date || '')}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional certification notes..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(certifiedDate, notes)}
              disabled={isLoading || !certifiedDate}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Certifying...' : 'Certify Application'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ============================================================================
// REJECT MODAL
// ============================================================================

interface RejectModalProps {
  application: PaymentApplication;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const RejectModal: React.FC<RejectModalProps> = ({ application, onConfirm, onCancel, isLoading }) => {
  const [reason, setReason] = useState('');

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" onClick={onCancel} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center space-x-3 mb-5">
            <div className="flex items-center justify-center w-10 h-10 bg-red-50 rounded-full">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Reject Application</h3>
              <p className="text-xs text-gray-500">{application.applicationNumber}</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(reason)}
              disabled={isLoading || !reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Rejecting...' : 'Reject Application'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

const ClientInvoices: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [statusFilter, setStatusFilter] = useState<PaymentApplicationStatus | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<PaymentApplication | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // Modals
  const [certifyingApplication, setCertifyingApplication] = useState<PaymentApplication | null>(null);
  const [rejectingApplication, setRejectingApplication] = useState<PaymentApplication | null>(null);
  const [recordingPaymentApp, setRecordingPaymentApp] = useState<PaymentApplication | null>(null);

  // Action menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API queries
  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useGetClientInvoicesQuery(
    {
      projectId: projectId || '',
      params: {
        status: statusFilter || undefined,
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      },
    },
    { skip: !projectId }
  );

  // Mutations
  const [submitApplication, { isLoading: isSubmitting }] = useSubmitClientInvoiceMutation();
  const [certifyApplication, { isLoading: isCertifying }] = useCertifyClientInvoiceMutation();
  const [rejectApplication, { isLoading: isRejecting }] = useRejectClientInvoiceMutation();
  const [recordPayment, { isLoading: isRecordingPayment }] = useRecordClientInvoicePaymentMutation();
  const [deleteApplication, { isLoading: isDeleting }] = useDeleteClientInvoiceMutation();

  const applications = response?.data?.applications || [];
  const pagination = response?.data?.pagination;
  const stats = response?.data?.stats;

  // --------------------------------------------------------------------------
  // ACTIONS
  // --------------------------------------------------------------------------

  const handleCreate = useCallback(() => {
    setSelectedApplication(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((app: PaymentApplication) => {
    setSelectedApplication(app);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (app: PaymentApplication) => {
      if (!projectId) return;
      const result = await submitApplication({ projectId, applicationId: app._id });
      if ('error' in result) {
        toast.error('Failed to submit payment application');
      } else {
        toast.success('Payment application submitted for review');
      }
    },
    [projectId, submitApplication, toast]
  );

  const handleCertifyConfirm = useCallback(
    async (certifiedDate: string, notes: string) => {
      if (!projectId || !certifyingApplication) return;
      const result = await certifyApplication({
        projectId,
        applicationId: certifyingApplication._id,
        data: { certifiedDate, notes: notes || undefined },
      });
      if ('error' in result) {
        toast.error('Failed to certify payment application');
      } else {
        toast.success('Payment application certified successfully');
        setCertifyingApplication(null);
      }
    },
    [projectId, certifyingApplication, certifyApplication, toast]
  );

  const handleRejectConfirm = useCallback(
    async (reason: string) => {
      if (!projectId || !rejectingApplication) return;
      const result = await rejectApplication({
        projectId,
        applicationId: rejectingApplication._id,
        data: { rejectionReason: reason },
      });
      if ('error' in result) {
        toast.error('Failed to reject payment application');
      } else {
        toast.success('Payment application rejected');
        setRejectingApplication(null);
      }
    },
    [projectId, rejectingApplication, rejectApplication, toast]
  );

  const handleRecordPaymentConfirm = useCallback(
    async (paidAmount: number, paidDate: string, paymentReference: string) => {
      if (!projectId || !recordingPaymentApp) return;
      const result = await recordPayment({
        projectId,
        applicationId: recordingPaymentApp._id,
        data: {
          paidAmount,
          paidDate,
          paymentReference: paymentReference || undefined,
        },
      });
      if ('error' in result) {
        toast.error('Failed to record payment');
      } else {
        toast.success('Payment recorded successfully');
        setRecordingPaymentApp(null);
      }
    },
    [projectId, recordingPaymentApp, recordPayment, toast]
  );

  const handleDownloadPdf = useCallback(
    async (app: PaymentApplication) => {
      if (!projectId) return;
      const token = localStorage.getItem('auth_token');
      const url = `/api/projects/${projectId}/client-invoices/${app._id}/pdf`;
      try {
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('PDF download failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `payment-application-${app.applicationNumber}.pdf`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  const handleDelete = useCallback(
    async (app: PaymentApplication) => {
      if (!projectId) return;
      if (!window.confirm(`Delete payment application ${app.applicationNumber}? This cannot be undone.`)) {
        return;
      }
      const result = await deleteApplication({ projectId, applicationId: app._id });
      if ('error' in result) {
        toast.error('Failed to delete payment application');
      } else {
        toast.success('Payment application deleted');
      }
    },
    [projectId, deleteApplication, toast]
  );

  // --------------------------------------------------------------------------
  // ACTION MENU ITEMS
  // --------------------------------------------------------------------------

  const getActionItems = useCallback(
    (app: PaymentApplication): (ActionMenuItem | 'divider')[] => {
      const isDraft = app.status === 'draft';
      const isSubmitted = app.status === 'submitted' || app.status === 'reviewed';
      const isCertified = app.status === 'certified';
      const isEditable = isDraft || app.status === 'rejected';
      const isPaid = app.status === 'paid';
      const isVoid = app.status === 'void';

      return [
        {
          label: 'View Details',
          icon: <FileText className="h-3.5 w-3.5" />,
          onClick: () => handleEdit(app),
        },
        {
          label: 'Edit',
          icon: <Pencil className="h-3.5 w-3.5" />,
          onClick: () => handleEdit(app),
          hidden: !isEditable,
        },
        'divider',
        {
          label: 'Submit for Review',
          icon: <Send className="h-3.5 w-3.5" />,
          onClick: () => handleSubmit(app),
          hidden: !isEditable,
          variant: 'default' as const,
        },
        {
          label: 'Certify',
          icon: <BadgeCheck className="h-3.5 w-3.5" />,
          onClick: () => setCertifyingApplication(app),
          hidden: !isSubmitted,
          variant: 'success' as const,
        },
        {
          label: 'Reject',
          icon: <XCircle className="h-3.5 w-3.5" />,
          onClick: () => setRejectingApplication(app),
          hidden: !isSubmitted,
          variant: 'danger' as const,
        },
        {
          label: 'Record Payment',
          icon: <CheckCircle2 className="h-3.5 w-3.5" />,
          onClick: () => setRecordingPaymentApp(app),
          hidden: !isCertified,
          variant: 'success' as const,
        },
        'divider',
        {
          label: 'Download PDF',
          icon: <Download className="h-3.5 w-3.5" />,
          onClick: () => handleDownloadPdf(app),
        },
        'divider',
        {
          label: 'Delete',
          icon: <Trash2 className="h-3.5 w-3.5" />,
          onClick: () => handleDelete(app),
          hidden: isPaid || isVoid,
          variant: 'danger' as const,
        },
      ];
    },
    [handleEdit, handleSubmit, handleDownloadPdf, handleDelete]
  );

  // --------------------------------------------------------------------------
  // STATS CARDS
  // --------------------------------------------------------------------------

  const statsConfig = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total Billed',
        value: formatCurrency(stats.totalBilled),
        icon: <TrendingUp className="h-4 w-4" />,
        variant: 'brand' as const,
      },
      {
        title: 'Collected',
        value: formatCurrency(stats.totalCollected),
        icon: <CheckCircle2 className="h-4 w-4" />,
        variant: 'success' as const,
      },
      {
        title: 'Outstanding',
        value: formatCurrency(stats.totalOutstanding),
        icon: <ClipboardList className="h-4 w-4" />,
        variant: 'warning' as const,
      },
      {
        title: 'Retainage Held',
        value: formatCurrency(stats.totalRetainageHeld),
        icon: <DollarSign className="h-4 w-4" />,
        variant: 'info' as const,
      },
    ];
  }, [stats]);

  // --------------------------------------------------------------------------
  // FILTERS
  // --------------------------------------------------------------------------

  const filtersNode = (
    <div className="flex items-center space-x-3">
      <div className="w-48">
        <SearchableDropdown
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={val => {
            setStatusFilter((val as PaymentApplicationStatus | '') || '');
            setPage(1);
          }}
          placeholder="All Statuses"
        />
      </div>
    </div>
  );

  // --------------------------------------------------------------------------
  // PAGINATION
  // --------------------------------------------------------------------------

  const paginationNode = pagination && pagination.totalPages > 1 ? (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
      <p className="text-xs text-gray-500">
        Showing {(page - 1) * limit + 1}–
        {Math.min(page * limit, pagination.total)} of {pagination.total} applications
      </p>
      <div className="flex items-center space-x-1">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-40 rounded"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs text-gray-600 px-2">
          {page} / {pagination.totalPages}
        </span>
        <button
          onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
          disabled={page === pagination.totalPages}
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-40 rounded"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  ) : null;

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <ModuleLayout
      title={projectId ? 'Client Invoices' : 'All Client Invoices'}
      description={
        projectId
          ? 'Payment applications and AIA G702/G703 billing for this project'
          : 'Manage client invoices and payment applications across all projects'
      }
      icon={<DollarSign className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Application',
        onClick: handleCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={statsConfig}
      filters={filtersNode}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={paginationNode}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : applications.length === 0 ? (
        <EmptyState
          icon={DollarSign}
          title="No payment applications found"
          description={
            statusFilter
              ? `No applications with status "${getStatusLabel(statusFilter as PaymentApplicationStatus)}"`
              : 'Create your first payment application to start the AIA G702/G703 billing process.'
          }
          action={
            !statusFilter
              ? { label: 'New Payment Application', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[2.5rem_6rem_1fr_1fr_1fr_1fr_1fr_5rem_3rem] bg-gray-50 border-b border-gray-200">
            {[
              '',
              'App #',
              'Billing Period',
              'Contract Sum',
              'This Period',
              'Payment Due',
              'Status',
              'Date',
              '',
            ].map((col, i) => (
              <div
                key={i}
                className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {col}
              </div>
            ))}
          </div>

          {/* Table Body */}
          {applications.map((app, index) => {
            const isMenuOpen = activeMenuId === app._id;
            const actionItems = getActionItems(app);

            return (
              <div
                key={app._id}
                className={`grid grid-cols-[2.5rem_6rem_1fr_1fr_1fr_1fr_1fr_5rem_3rem] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors group`}
              >
                {/* Row number */}
                <div className="flex items-center justify-center px-2 text-xs text-gray-400">
                  {(page - 1) * limit + index + 1}
                </div>

                {/* Application Number */}
                <div className="flex items-center px-3 py-3">
                  <button
                    type="button"
                    onClick={() => handleEdit(app)}
                    className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                  >
                    {app.applicationNumber}
                  </button>
                </div>

                {/* Billing Period */}
                <div className="flex items-center px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800">
                      {formatDate(app.billingPeriodFrom)}
                    </p>
                    <p className="text-xs text-gray-400">
                      to {formatDate(app.billingPeriodTo)}
                    </p>
                  </div>
                </div>

                {/* Contract Sum to Date */}
                <div className="flex items-center px-3 py-3">
                  <p className="text-xs text-gray-700">{formatCurrency(app.contractSumToDate)}</p>
                </div>

                {/* Total Completed This Period */}
                <div className="flex items-center px-3 py-3">
                  <p className="text-xs text-gray-700">
                    {formatCurrency(app.totalCompletedAndStored)}
                  </p>
                </div>

                {/* Current Payment Due */}
                <div className="flex items-center px-3 py-3">
                  <p
                    className={`text-xs font-semibold ${
                      app.status === 'paid'
                        ? 'text-green-600'
                        : app.status === 'rejected'
                          ? 'text-red-500 line-through'
                          : 'text-gray-800'
                    }`}
                  >
                    {formatCurrency(app.currentPaymentDue)}
                  </p>
                </div>

                {/* Status */}
                <div className="flex items-center px-3 py-3">
                  <StatusBadge
                    status={getStatusLabel(app.status)}
                    variant={getStatusVariant(app.status)}
                  />
                </div>

                {/* Date */}
                <div className="flex items-center px-3 py-3">
                  <p className="text-xs text-gray-500">{formatDate(app.createdAt)}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center px-2">
                  <ActionMenu
                    id={app._id}
                    items={actionItems}
                    isOpen={isMenuOpen}
                    onOpenChange={open => setActiveMenuId(open ? app._id : null)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drawers & Modals */}
      <ClientInvoiceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        application={selectedApplication}
        mode={drawerMode}
        onSuccess={refetch}
      />

      {certifyingApplication && (
        <CertifyModal
          application={certifyingApplication}
          onConfirm={handleCertifyConfirm}
          onCancel={() => setCertifyingApplication(null)}
          isLoading={isCertifying}
        />
      )}

      {rejectingApplication && (
        <RejectModal
          application={rejectingApplication}
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectingApplication(null)}
          isLoading={isRejecting}
        />
      )}

      {recordingPaymentApp && (
        <RecordPaymentModal
          application={recordingPaymentApp}
          onConfirm={handleRecordPaymentConfirm}
          onCancel={() => setRecordingPaymentApp(null)}
          isLoading={isRecordingPayment}
        />
      )}
    </ModuleLayout>
  );
};

export default ClientInvoices;
